import { FunctionComponent, TextareaHTMLAttributes } from 'react';
import { Label } from '@atoms/index';
import { InlineErrorMessage } from '@atoms/Errors/InlineErrorMessage';
import { ErrorMessages } from '@types';
import { cx } from 'class-variance-authority';

export interface TextAreaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  labelText: string;
  isLabelTextTitle?: boolean;
  description?: string | JSX.Element;
  errorPath?: string;
  errorMessages?: ErrorMessages[];
  maxLength?: number;
  subtext?: string;
  showCounter?: boolean;
}

interface CharacterCounterProps {
  maxLength: number;
  currentLength: number;
  showCounter?: boolean;
}

const CharacterCounter: React.FC<CharacterCounterProps> = ({
  maxLength,
  currentLength,
  showCounter = true,
}) => {
  if (!maxLength || !showCounter) {
    return null;
  }
  const isExceeded = Number(currentLength) > Number(maxLength);
  const message = isExceeded
    ? `You have ${Number(currentLength) - Number(maxLength)} characters too many.`
    : `You have ${Number(maxLength) - Number(currentLength)} characters remaining.`;
  const fontStyle = isExceeded
    ? `text-status-error font-bold`
    : `text-text-body font-normal`;

  return <div className={`${fontStyle} text-body mt-1`}>{message}</div>;
};

export const TextArea: FunctionComponent<TextAreaProps> = (props) => {
  const maxLength = props.maxLength ?? 500;
  const noErrorBool =
    !props.errorMessages ||
    props.errorMessages.length === 0 ||
    props.errorMessages.every((error) => !error.message);
  const isExceeded =
    typeof props.value === 'string' &&
    Number(props.value?.length) > Number(maxLength);
  const textboxStyle =
    isExceeded || !noErrorBool
      ? '!border-status-error shadow-focus !shadow-status-error'
      : '';
  return (
    <div className="my-4">
      <Label
        text={props.labelText}
        className="!text-brand-primary"
        isTitle={props.isLabelTextTitle ?? true}
        htmlFor={props.id}
        id={props.id}
      />
      <div
        className={cx(
          'max-w-form w-full',
          `${props.isLabelTextTitle ? 'mt-6 flex flex-col gap-y-6' : ''}`,
          props.className,
        )}
      >
        {props.subtext ? (
          <pre className="body-1 max-w-form whitespace-pre-wrap">
            {props.subtext}
          </pre>
        ) : null}

        <div
          data-testid="description"
          className={`text-text-body text-bodyLg pb-2 font-normal leading-[28.5px] ${noErrorBool ? 'mb-0' : 'mb-4'}`}
        >
          {props.description}
        </div>
        <InlineErrorMessage
          path={props.errorPath}
          messages={props.errorMessages}
        />
        <textarea
          {...props}
          className={`text-body bg-background-default border-ui-border max-w-form focus:shadow-focus w-full appearance-none rounded border p-2 placeholder-current outline-none ${textboxStyle}
          block min-h-[104px] ${props.disabled ? 'text-text-disabled border-ui-disabled cursor-not-allowed cursor-not-allowed' : 'text-text-body'}
          `}
          maxLength={undefined}
        />
        <CharacterCounter
          maxLength={maxLength}
          currentLength={
            typeof props.value === 'string' ? props.value.length : 0
          }
          showCounter={props.showCounter}
        />
      </div>
    </div>
  );
};
